import { supabase } from '../lib/supabase';

export const maturationService = {
  async getGuardianProfile() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('guardian_profiles')?.select('*')?.eq('id', user?.id)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    if (!data) return null;

    return {
      id: data?.id,
      consentStatus: data?.consent_status,
      consentAcceptedAt: data?.consent_accepted_at,
      unitsPreference: data?.units_preference,
      measurementFrequency: data?.measurement_frequency,
      nextMeasurementDate: data?.next_measurement_date,
      reminderEnabled: data?.reminder_enabled,
      lastReminderSentAt: data?.last_reminder_sent_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createGuardianProfile(consentAccepted) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('guardian_profiles')?.insert({
        id: user?.id,
        consent_status: consentAccepted ? 'accepted' : 'pending',
        consent_accepted_at: consentAccepted ? new Date()?.toISOString() : null,
        units_preference: 'metric'
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      consentStatus: data?.consent_status,
      consentAcceptedAt: data?.consent_accepted_at,
      unitsPreference: data?.units_preference,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateGuardianProfile(updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.consentStatus) dbUpdates.consent_status = updates?.consentStatus;
    if (updates?.consentAcceptedAt) dbUpdates.consent_accepted_at = updates?.consentAcceptedAt;
    if (updates?.unitsPreference) dbUpdates.units_preference = updates?.unitsPreference;
    if (updates?.measurementFrequency) dbUpdates.measurement_frequency = updates?.measurementFrequency;
    if (updates?.nextMeasurementDate) dbUpdates.next_measurement_date = updates?.nextMeasurementDate;
    if (updates?.reminderEnabled !== undefined) dbUpdates.reminder_enabled = updates?.reminderEnabled;

    const { data, error } = await supabase?.from('guardian_profiles')?.update(dbUpdates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      consentStatus: data?.consent_status,
      consentAcceptedAt: data?.consent_accepted_at,
      unitsPreference: data?.units_preference,
      measurementFrequency: data?.measurement_frequency,
      nextMeasurementDate: data?.next_measurement_date,
      reminderEnabled: data?.reminder_enabled,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getChildren() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('child_profiles')?.select('*')?.eq('guardian_id', user?.id)?.eq('is_active', true)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(child => ({
      id: child?.id,
      guardianId: child?.guardian_id,
      nickname: child?.nickname,
      sexAtBirth: child?.sex_at_birth,
      dateOfBirth: child?.date_of_birth,
      sportTags: child?.sport_tags || [],
      isActive: child?.is_active,
      createdAt: child?.created_at,
      updatedAt: child?.updated_at
    }));
  },

  async getChildById(childId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('child_profiles')?.select('*')?.eq('id', childId)?.eq('guardian_id', user?.id)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      guardianId: data?.guardian_id,
      nickname: data?.nickname,
      sexAtBirth: data?.sex_at_birth,
      dateOfBirth: data?.date_of_birth,
      sportTags: data?.sport_tags || [],
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async createChild(childData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('child_profiles')?.insert({
        guardian_id: user?.id,
        nickname: childData?.nickname,
        sex_at_birth: childData?.sexAtBirth,
        date_of_birth: childData?.dateOfBirth,
        sport_tags: childData?.sportTags || []
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      guardianId: data?.guardian_id,
      nickname: data?.nickname,
      sexAtBirth: data?.sex_at_birth,
      dateOfBirth: data?.date_of_birth,
      sportTags: data?.sport_tags || [],
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateChild(childId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbUpdates = {};
    if (updates?.nickname) dbUpdates.nickname = updates?.nickname;
    if (updates?.sportTags) dbUpdates.sport_tags = updates?.sportTags;

    const { data, error } = await supabase?.from('child_profiles')?.update(dbUpdates)?.eq('id', childId)?.eq('guardian_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      guardianId: data?.guardian_id,
      nickname: data?.nickname,
      sexAtBirth: data?.sex_at_birth,
      dateOfBirth: data?.date_of_birth,
      sportTags: data?.sport_tags || [],
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteChild(childId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // First delete all measurements for this child
    const { error: measurementsError } = await supabase
      ?.from('measurement_records')
      ?.delete()
      ?.eq('child_id', childId);

    if (measurementsError) throw measurementsError;

    // Then delete the child profile
    const { error } = await supabase?.from('child_profiles')?.delete()?.eq('id', childId)?.eq('guardian_id', user?.id);

    if (error) throw error;
  },

  async deleteAccount() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get all children for this guardian
    const children = await this.getChildren();

    // Delete all measurements for all children
    for (const child of children) {
      const { error: measurementsError } = await supabase
        ?.from('measurement_records')
        ?.delete()
        ?.eq('child_id', child?.id);
      
      if (measurementsError) throw measurementsError;
    }

    // Delete all child profiles
    const { error: childrenError } = await supabase
      ?.from('child_profiles')
      ?.delete()
      ?.eq('guardian_id', user?.id);

    if (childrenError) throw childrenError;

    // Delete guardian profile
    const { error: guardianError } = await supabase
      ?.from('guardian_profiles')
      ?.delete()
      ?.eq('id', user?.id);

    if (guardianError) throw guardianError;

    // Delete user profile
    const { error: userProfileError } = await supabase
      ?.from('user_profiles')
      ?.delete()
      ?.eq('id', user?.id);

    if (userProfileError) throw userProfileError;

    // Finally delete the auth user account
    const { error: authError } = await supabase?.auth?.admin?.deleteUser(user?.id);
    
    if (authError) {
      // If admin delete fails (requires service role), sign out the user
      await supabase?.auth?.signOut();
    }
  },

  async getMeasurements(childId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('measurement_records')?.select('*')?.eq('child_id', childId)?.order('measurement_datetime', { ascending: true });

    if (error) throw error;

    return data?.map(m => ({
      id: m?.id,
      childId: m?.child_id,
      measurementDatetime: m?.measurement_datetime,
      timezone: m?.timezone,
      standingHeightCm: m?.standing_height_cm,
      bodyMassKg: m?.body_mass_kg,
      sittingHeightCm: m?.sitting_height_cm,
      heightReplicate1Cm: m?.height_replicate_1_cm,
      heightReplicate2Cm: m?.height_replicate_2_cm,
      heightMeanCm: m?.height_mean_cm,
      heightDifferenceCm: m?.height_difference_cm,
      measurementNotes: m?.measurement_notes,
      dataQualityGrade: m?.data_quality_grade,
      createdAt: m?.created_at,
      updatedAt: m?.updated_at
    }));
  },

  async createMeasurement(measurementData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('measurement_records')?.insert({
        child_id: measurementData?.childId,
        measurement_datetime: measurementData?.measurementDatetime,
        timezone: measurementData?.timezone || 'UTC',
        standing_height_cm: measurementData?.standingHeightCm,
        body_mass_kg: measurementData?.bodyMassKg,
        sitting_height_cm: measurementData?.sittingHeightCm,
        height_replicate_1_cm: measurementData?.heightReplicate1Cm,
        height_replicate_2_cm: measurementData?.heightReplicate2Cm,
        measurement_notes: measurementData?.measurementNotes
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      childId: data?.child_id,
      measurementDatetime: data?.measurement_datetime,
      timezone: data?.timezone,
      standingHeightCm: data?.standing_height_cm,
      bodyMassKg: data?.body_mass_kg,
      sittingHeightCm: data?.sitting_height_cm,
      heightReplicate1Cm: data?.height_replicate_1_cm,
      heightReplicate2Cm: data?.height_replicate_2_cm,
      heightMeanCm: data?.height_mean_cm,
      heightDifferenceCm: data?.height_difference_cm,
      measurementNotes: data?.measurement_notes,
      dataQualityGrade: data?.data_quality_grade,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async deleteMeasurement(measurementId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('measurement_records')?.delete()?.eq('id', measurementId);

    if (error) throw error;
  },

  async exportChildDataCSV(childId) {
    const child = await this.getChildById(childId);
    const measurements = await this.getMeasurements(childId);

    const headers = ['Date', 'Standing Height (cm)', 'Body Mass (kg)', 'Sitting Height (cm)', 'Replicate 1 (cm)', 'Replicate 2 (cm)', 'Mean Height (cm)', 'Difference (cm)', 'Notes'];
    const rows = measurements?.map(m => [
      new Date(m?.measurementDatetime)?.toLocaleDateString('en-GB'),
      m?.standingHeightCm,
      m?.bodyMassKg || '',
      m?.sittingHeightCm || '',
      m?.heightReplicate1Cm || '',
      m?.heightReplicate2Cm || '',
      m?.heightMeanCm || '',
      m?.heightDifferenceCm || '',
      m?.measurementNotes || ''
    ]);

    const csvContent = [headers, ...rows]?.map(row => row?.join(','))?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${child?.nickname}_growth_data_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  }
};